"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { ShoppingCart, User2, ChevronDown } from "lucide-react";
import SideCartV2 from "@/components/common/SideCartV2";
import { useHeaderCache } from "./HeaderCacheContext";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { Currency } from "@/packages/types/currency";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { useCart } from "@/packages/context/CartContext";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";

export default function UserActionsV2() {
  const [mounted, setMounted] = useState(false);
  const { currencies: currencyOptions } = useHeaderCache();

  useEffect(() => {
    setMounted(true);
  }, []);

  const { code: currencyCode, changeCurrency, isInitialized } = useCurrency();
  const { isLoggedIn } = useAuth();
  const isLoggedInState = mounted ? isLoggedIn : false;
  const { fastPush } = useFastRouter();
  const { cartItems, isCartOpen, setIsCartOpen } = useCart();
  const searchParams = useSearchParams();

  const { colors, typography } = HEADER_V2_DESIGN_SYSTEM;

  // 1. Sync URL param when cached currencies are available
  useEffect(() => {
    if (currencyOptions.length === 0) return;

    const currencyFromUrl = searchParams.get("currency");
    let initialCurrency: Currency | undefined;
    if (currencyFromUrl) {
      initialCurrency = currencyOptions.find((c) => c.code === currencyFromUrl);
    }
    if (!initialCurrency) {
      initialCurrency =
        currencyOptions.find((c) => c.code === currencyCode) ||
        currencyOptions.find((c) => c.isDefault) ||
        currencyOptions[0];
    }

    if (initialCurrency) {
      const currencyFromUrl = searchParams.get("currency");
      if (currencyFromUrl || isInitialized) {
        changeCurrency({
          id: initialCurrency.id,
          symbol: initialCurrency.symbol,
          code: initialCurrency.code,
          exchangeRate: initialCurrency.exchangeRate,
        });
      }
    }
  }, [currencyOptions, searchParams, changeCurrency, currencyCode, isInitialized]);

  // 2. Handle currency selection
  const handleCurrencyChange = (val: string) => {
    const selected = currencyOptions.find((c) => c.code === val);
    if (selected) {
      changeCurrency({
        id: selected.id,
        symbol: selected.symbol,
        code: selected.code,
        exchangeRate: selected.exchangeRate,
      });

      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", val);
      fastPush(`?${params.toString()}`);
    }
  };

  return (
    <>
      <div className={`flex items-center gap-5 md:gap-[30px] ${colors.secondaryText} ${typography.fontFamilySans} text-sm font-semibold shrink-0`}>
        
        {/* Currency Selector - Styled as a borderless transparent select (Hidden as requested) */}
        <div className="hidden relative items-center cursor-pointer">
          {!mounted ? (
            <div className="flex items-center gap-1 text-slate-500 text-xs font-semibold h-auto">
              <span>INR</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-50" />
            </div>
          ) : (
            <Select value={currencyCode} onValueChange={handleCurrencyChange}>
              <SelectTrigger
                aria-label="Change currency"
                className="flex items-center gap-1 !border-none bg-transparent !shadow-none hover:text-[var(--brand-accent)] transition-colors duration-200 !outline-none !ring-0 focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none focus-visible:!border-none data-[state=open]:!border-none data-[state=open]:!ring-0 data-[state=open]:!outline-none p-0 cursor-pointer text-xs font-semibold h-auto"
              >
                <SelectValue placeholder={currencyCode || "INR"} />
              </SelectTrigger>

              <SelectContent className="z-[999] bg-white border border-slate-200 rounded-md shadow-md">
                {currencyOptions.map((c) => (
                  <SelectItem key={c.id} value={c.code} className="text-xs font-semibold cursor-pointer">
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Store Location Finder */}


        {/* Account login/profile */}
        <button
          onClick={() => {
            if (isLoggedIn) fastPush("/user-profile");
            else fastPush("/sign-in");
          }}
          title={isLoggedInState ? "Profile" : "Sign in"}
          className="hidden md:flex items-center gap-1.5 hover:text-[var(--brand-accent)] transition-colors duration-200 cursor-pointer"
        >
          <User2 className="w-[16px] h-[16px] text-slate-500" />
          <span className="hidden md:inline text-xs font-semibold">
            {isLoggedInState ? "Profile" : "Login"}
          </span>
        </button>

        <button
          onClick={() => setIsCartOpen(true)}
          title="Shopping cart"
          className="relative flex items-center gap-2.5 text-slate-700 hover:text-[var(--brand-accent)] transition-colors duration-200 cursor-pointer"
        >
          <div className="relative pr-1">
            <ShoppingCart className="w-[23px] h-[23px] stroke-[1.8] text-current" />
            <span className="absolute top-[-4px] right-[0px] inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-[var(--brand-accent)] px-1 text-[8px] font-bold text-white shadow-xs">
              {mounted ? cartItems.length : 0}
            </span>
          </div>
          <span className="hidden md:inline text-[13px] font-semibold text-current">Cart</span>
        </button>

      </div>

      {/* Side Cart Drawer Wrapper */}
      {isCartOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-xs transition-opacity duration-300"
          onClick={() => setIsCartOpen(false)}
        >
          <div
            className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-2xl z-[1000] transform transition-transform duration-300 translate-x-0"
            onClick={(e) => e.stopPropagation()}
          >
            <SideCartV2 onClose={() => setIsCartOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}
